/* KERABOX | Muràn care guide: collection-wide matching via Shopify native section rendering. */
(() => {
  const start = () => {
    const root = document.querySelector('[data-kb-muran-guide]');
    if (!root || root.dataset.kbGuideReady === 'true') return;
    root.dataset.kbGuideReady = 'true';

    const areaButtons = Array.from(root.querySelectorAll('[data-kb-area]'));
    const needButtons = Array.from(root.querySelectorAll('[data-kb-need]'));
    const stage = root.querySelector('[data-kb-stage]');
    const panels = Array.from(root.querySelectorAll('[data-kb-options]'));
    const result = root.querySelector('[data-kb-guide-result]');
    const count = root.querySelector('[data-kb-guide-count]');
    const reset = root.querySelector('[data-kb-reset]');
    const groups = {
      hair: ['shine', 'repair', 'color', 'curls', 'volume'],
      scalp: ['dandruff', 'oily', 'sensitive', 'thinning', 'cleanse'],
      body: ['face', 'bodycare']
    };

    let area = '';
    let need = '';
    let currentList = null;
    let originalCount = '';
    let currentRequest = 0;
    let controller = null;

    const listOnPage = () => document.querySelector('.shopify-section--main-collection product-list[data-kb-muran-section]');
    const isMatch = card => !!need &&
      (card.dataset.kbMuranTags || '').split(/\s+/).includes(need) &&
      card.dataset.kbMuranAvailable !== 'false';

    const updateButtons = () => {
      areaButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.kbArea === area)));
      needButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.kbNeed === need)));
      stage.hidden = !area;
      panels.forEach(panel => { panel.hidden = panel.dataset.kbOptions !== area; });
    };

    const undoFilter = list => {
      if (!list) return;
      list.querySelectorAll(':scope > product-card[data-kb-guide-added]').forEach(card => card.remove());
      list.querySelectorAll(':scope > product-card[data-kb-muran-tags]').forEach(card => {
        card.hidden = false;
        card.style.display = '';
      });
      list.closest('.collection__main')?.classList.remove('kb-muran-guide-active');
    };

    const scrollToProducts = () => {
      const target = document.querySelector('.shopify-section--main-collection .collection-toolbar') || listOnPage();
      if (!target) return;
      const styles = window.getComputedStyle(document.documentElement);
      const headerHeight = Number.parseFloat(styles.getPropertyValue('--sticky-area-height')) || 90;
      const offset = Math.max(64, Math.min(headerHeight + 12, 190));
      const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset);
      if (Math.abs(top - window.scrollY) < 64) return;
      window.scrollTo({
        top,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    };

    const apply = async (scrollAfter = false) => {
      const requestId = ++currentRequest;
      if (controller) controller.abort();
      controller = null;

      const list = listOnPage();
      const toolbar = document.querySelector('.shopify-section--main-collection .collection-toolbar__products-count');

      if (list !== currentList) {
        currentList = list;
        originalCount = toolbar ? toolbar.textContent : '';
      }

      undoFilter(list);
      result.hidden = !need;
      result.setAttribute('aria-busy', 'false');

      if (!need) {
        if (toolbar && originalCount) toolbar.textContent = originalCount;
        count.textContent = '';
        if (scrollAfter) scrollToProducts();
        return;
      }
      if (!list) {
        count.textContent = 'Ingen produkter med de valgte filtrene.';
        return;
      }

      const selected = needButtons.find(button => button.dataset.kbNeed === need);
      const label = selected ? selected.textContent.trim() : '';
      const baseCards = Array.from(list.querySelectorAll(':scope > product-card[data-kb-muran-tags]'));
      const selectedBase = baseCards.filter(isMatch);
      baseCards.forEach(card => {
        const matches = isMatch(card);
        card.hidden = !matches;
        card.style.display = matches ? '' : 'none';
      });

      const main = list.closest('.collection__main');
      main?.classList.add('kb-muran-guide-active');
      count.textContent = 'Finner produkter for ' + label.toLowerCase() + '…';
      result.setAttribute('aria-busy', 'true');

      const pages = Math.max(1, Number.parseInt(list.dataset.kbMuranPages || '1', 10) || 1);
      const currentPage = Math.max(1, Number.parseInt(list.dataset.kbMuranCurrentPage || '1', 10) || 1);
      const sectionId = list.dataset.kbMuranSection;
      const otherPages = [];
      for (let i = 1; i <= pages; i++) {
        if (i !== currentPage) otherPages.push(i);
      }

      const abort = new AbortController();
      controller = abort;
      try {
        const fetched = await Promise.all(otherPages.map(async page => {
          const url = new URL(window.location.href);
          url.searchParams.set('page', String(page));
          url.searchParams.set('section_id', sectionId);
          url.searchParams.delete('sections');
          const response = await fetch(url.toString(), {
            signal: abort.signal,
            headers: { 'Accept': 'text/html' }
          });
          if (!response.ok) throw new Error('Could not fetch page ' + page);
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const remote = parsed.querySelector('product-list[data-kb-muran-section]');
          if (!remote) throw new Error('Missing page ' + page);
          const matches = Array.from(remote.querySelectorAll(':scope > product-card[data-kb-muran-tags]'))
            .filter(isMatch)
            .map(card => {
              const item = document.importNode(card, true);
              item.setAttribute('data-kb-guide-added', '');
              /* Prestige keeps dynamically imported cards invisible while
                 reveal-on-scroll remains set; show them immediately. */
              item.removeAttribute('reveal-on-scroll');
              item.querySelectorAll('[reveal-on-scroll]').forEach(child => child.removeAttribute('reveal-on-scroll'));
              item.hidden = false;
              item.style.display = '';
              return item;
            });
          return { page, matches };
        }));
        if (requestId !== currentRequest || list !== listOnPage()) return;

        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        let total = selectedBase.length;
        fetched.sort((a, b) => a.page - b.page).forEach(batch => {
          const fragment = batch.page < currentPage ? before : after;
          batch.matches.forEach(card => { fragment.appendChild(card); total++; });
        });
        list.insertBefore(before, list.firstChild);
        list.appendChild(after);

        const message = total
          ? total + ' produkter fra Muràn · ' + label
          : 'Ingen tilgjengelige produkter for ' + label.toLowerCase() + ' med de valgte filtrene.';
        count.textContent = message;
        if (toolbar) toolbar.textContent = total + ' produkter';
        if (scrollAfter) scrollToProducts();
      } catch (error) {
        if (requestId !== currentRequest || error.name === 'AbortError') return;
        count.textContent = 'Kunne ikke vise alle produktene. Velg behovet på nytt eller vis alle produkter.';
        if (toolbar && originalCount) toolbar.textContent = originalCount;
      } finally {
        if (requestId === currentRequest) {
          result.setAttribute('aria-busy', 'false');
          controller = null;
        }
      }
    };

    const selectArea = next => {
      if (!Object.prototype.hasOwnProperty.call(groups, next)) return;
      area = next;
      need = '';
      updateButtons();
      void apply();
    };
    const selectNeed = next => {
      if (!area || !groups[area].includes(next)) return;
      need = next;
      updateButtons();
      void apply(true);
    };
    areaButtons.forEach(button => button.addEventListener('click', () => selectArea(button.dataset.kbArea)));
    needButtons.forEach(button => button.addEventListener('click', () => selectNeed(button.dataset.kbNeed)));
    /* A real collection link works even when JavaScript cannot handle a reset.
       Keep theme-preview context and jump to the native product grid. */
    const allProductsUrl = new URL(root.dataset.kbCollectionUrl || window.location.pathname, window.location.origin);
    const previewTheme = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewTheme) allProductsUrl.searchParams.set('preview_theme_id', previewTheme);
    const nativeGrid = listOnPage();
    if (nativeGrid?.id) allProductsUrl.hash = nativeGrid.id;
    reset.href = allProductsUrl.pathname + allProductsUrl.search + allProductsUrl.hash;
    reset.addEventListener('click', event => {
      if (!need) return;
      event.preventDefault();
      area = '';
      need = '';
      updateButtons();
      void apply(true);
    });
    updateButtons();
    void apply();

    /* Prestige replaces collection results during sorting, filtering and pagination. */
    let queued = false;
    const observer = new MutationObserver(() => {
      if (!need || queued || listOnPage() === currentList) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        if (need && listOnPage() !== currentList) void apply();
      });
    });
    observer.observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
